<script setup lang="ts">
import AppLayout from '../layouts/AppLayout.vue'
import { ref, onMounted } from 'vue'
import { useAuth } from '../composables/useAuth'
import { getSupplierProfile } from '../services/api'
import type { SupplierProfile } from '../types/supplier'
import { useCurrentContext } from '../composables/useCurrentContext'
import SupplierLogo from '../components/SupplierLogo.vue'

const { supplierId } = useCurrentContext()
const { getAccessTokenSilently } = useAuth()

const loading = ref(true)
const profile = ref<SupplierProfile | null>(null)
const error = ref<string | null>(null)

onMounted(async () => {
  try {
    if (!supplierId.value) throw new Error('No supplier context')
    const token = await getAccessTokenSilently()
    profile.value = await getSupplierProfile(supplierId.value, token)
  } catch (e: any) {
    console.error(e)
    error.value = "Failed to load company profile."
  } finally {
    loading.value = false
  }
})

const formatDate = (d: string) => new Date(d).toLocaleDateString()
</script>

<template>
  <AppLayout>
    <div v-if="loading" class="flex justify-center py-20">
      <svg class="animate-spin w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
    </div>
    
    <div v-else-if="error" class="bg-red-50 text-red-700 p-4 rounded-lg">
      {{ error }}
    </div>

    <div v-else-if="profile" class="max-w-7xl mx-auto space-y-8">
      <!-- Header Card -->
      <div class="bg-cc-elevated p-6 rounded-xl border border-cc-border shadow-sm flex flex-col md:flex-row gap-6 items-start">
        <SupplierLogo
          :src="profile.logoUrl"
          :alt="profile.name"
          class="w-24 h-24 rounded-lg shrink-0 object-contain"
        />
        <div class="flex-1">
          <h1 class="text-3xl font-bold text-cc-text">{{ profile.name }}</h1>
          <a v-if="profile.websiteUrl" :href="profile.websiteUrl" target="_blank" class="text-indigo-600 dark:text-indigo-400 hover:underline text-sm font-medium mt-1 inline-block">
            {{ profile.websiteUrl.replace(/^https?:\/\//, '') }} ↗
          </a>
          <p class="text-cc-text-secondary mt-3 max-w-2xl">{{ profile.summary }}</p>
          
          <div class="flex flex-wrap gap-2 mt-4">
            <span v-for="cat in profile.categories" :key="cat" class="bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 px-2.5 py-0.5 rounded text-xs font-semibold uppercase tracking-wide">
              {{ cat }}
            </span>
          </div>
        </div>
      </div>

      <!-- Two Column Layout -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        <!-- Left Column: Company & Contacts -->
        <div class="space-y-8">
          
          <!-- Overview -->
          <div class="bg-cc-elevated p-6 rounded-xl border border-cc-border shadow-sm">
            <h2 class="text-lg font-bold text-cc-text mb-4 border-b border-cc-border pb-2">Company Overview</h2>
            <div class="space-y-3 text-sm">
               <div>
                  <div class="text-cc-text-secondary text-xs uppercase font-semibold">Vertical Focus</div>
                  <div class="text-cc-text">{{ profile.verticalFocus ? profile.verticalFocus.join(', ') : 'General' }}</div>
               </div>
               <div v-if="profile.suppliersTxtUrl">
                 <div class="text-cc-text-secondary text-xs uppercase font-semibold">suppliers.txt</div>
                 <a :href="profile.suppliersTxtUrl" target="_blank" class="text-blue-600 dark:text-blue-400 hover:underline truncate block">View File</a>
               </div>
            </div>
          </div>

          <!-- Contacts -->
          <div class="bg-cc-elevated p-6 rounded-xl border border-cc-border shadow-sm">
            <h2 class="text-lg font-bold text-cc-text mb-4 border-b border-cc-border pb-2">Channel Contacts</h2>
            <div class="space-y-4">
              <div v-for="emp in profile.employees" :key="emp.id" class="flex items-start gap-3">
                <div class="w-8 h-8 rounded-full bg-cc-hover flex items-center justify-center text-xs font-bold text-cc-text-secondary">
                  {{ emp.name.charAt(0) }}
                </div>
                <div>
                  <div class="font-medium text-cc-text">{{ emp.name }}</div>
                  <div class="text-xs text-indigo-600 dark:text-indigo-400 font-medium mb-0.5">{{ emp.role }}</div>
                  <a :href="`mailto:${emp.email}`" class="text-xs text-cc-text-secondary hover:text-gray-900 dark:hover:text-white">{{ emp.email }}</a>
                </div>
              </div>
              <div v-if="(profile.employees || []).length === 0" class="text-cc-text-tertiary text-sm italic">No contacts listed.</div>
            </div>
          </div>

        </div>

        <!-- Right Column: Products & Training -->
        <div class="lg:col-span-2 space-y-8">
          
          <!-- Products -->
          <div class="bg-cc-elevated p-6 rounded-xl border border-cc-border shadow-sm">
            <div class="flex justify-between items-center mb-4 border-b border-cc-border pb-2">
               <h2 class="text-lg font-bold text-cc-text">Product Catalog</h2>
               <span class="text-xs text-cc-text-secondary bg-cc-hover px-2 py-1 rounded-full">{{ profile.products?.length || 0 }} Products</span>
            </div>
            
            <div class="grid sm:grid-cols-2 gap-4">
              <div v-for="prod in profile.products" :key="prod.id" class="border border-cc-border rounded-lg p-4 hover:border-indigo-200 dark:hover:border-indigo-500/50 transition bg-gray-50/50 dark:bg-gray-700/30">
                <div class="flex justify-between items-start mb-2">
                   <h3 class="font-bold text-cc-text">{{ prod.name }}</h3>
                   <span class="text-xs bg-gray-100 dark:bg-gray-600 text-cc-text-secondary px-1.5 py-0.5 rounded">{{ prod.pricingBand || 'Custom' }}</span>
                </div>
                <p class="text-sm text-cc-text-secondary line-clamp-2 mb-3">{{ prod.description }}</p>
                <div class="flex gap-2">
                   <span v-for="c in (prod.categories || []).slice(0,2)" :key="c" class="text-[10px] uppercase font-semibold text-cc-text-tertiary border border-gray-100 dark:border-gray-600 px-1.5 py-0.5 rounded">{{ c }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Promotions -->
          <div class="bg-cc-elevated p-6 rounded-xl border border-cc-border shadow-sm relative overflow-hidden">
             <div class="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                <svg class="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>
             </div>
             <h2 class="text-lg font-bold text-cc-text mb-4 border-b border-cc-border pb-2">Active Promotions</h2>
             <div class="space-y-3">
               <div v-for="promo in profile.promotions" :key="promo.id" class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-900/50 rounded-lg p-4">
                  <h3 class="font-bold text-yellow-900 dark:text-yellow-400">{{ promo.name }}</h3>
                  <p class="text-sm text-yellow-800 dark:text-yellow-300/80 mt-1">{{ promo.description }}</p>
                  <div class="text-xs text-yellow-600 dark:text-yellow-500 mt-2 font-mono">
                     Valid until: {{ promo.validUntil ? formatDate(promo.validUntil) : 'Ongoing' }}
                  </div>
               </div>
               <div v-if="(profile.promotions || []).length === 0" class="text-cc-text-tertiary text-sm italic">No active promotions.</div>
             </div>
          </div>

          <!-- Training -->
          <div class="bg-cc-elevated p-6 rounded-xl border border-cc-border shadow-sm">
            <h2 class="text-lg font-bold text-cc-text mb-4 border-b border-cc-border pb-2">Training & Enablement</h2>
            <div class="space-y-3">
              <div v-for="asset in profile.trainingAssets" :key="asset.id" class="flex items-center justify-between p-3 border border-cc-border rounded-lg hover:bg-cc-hover transition group">
                <div class="flex items-center gap-3">
                   <div :class="['w-8 h-8 rounded flex items-center justify-center text-xs font-bold uppercase', asset.assetType === 'Video' ? 'bg-cc-danger/10 text-red-700 dark:text-red-400' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400']">
                      {{ asset.assetType ? asset.assetType.substring(0,3) : 'DOC' }}
                   </div>
                   <div>
                      <div class="font-medium text-cc-text group-hover:text-indigo-700 dark:group-hover:text-indigo-400 transition">{{ asset.title }}</div>
                      <div v-if="asset.relatedProducts.length" class="text-xs text-cc-text-tertiary">
                         For: {{ asset.relatedProducts.map((p: any) => p.name).join(', ') }}
                      </div>
                   </div>
                </div>
                <a :href="asset.url" target="_blank" class="text-sm font-medium text-cc-text-tertiary hover:text-indigo-600 dark:hover:text-indigo-400 px-3 py-1 rounded border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800">Open</a>
              </div>
              <div v-if="(profile.trainingAssets || []).length === 0" class="text-cc-text-tertiary text-sm italic">No training assets available.</div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </AppLayout>
</template>
